<template>
  <div :class="classes.marketCard">
    <div :class="classes.header">{{ header }}</div>

    <div :class="[classes.offerWrapper, 'offer-card']">
      <div :class="[classes.offerText]" ref="offerTextRef">
        <p :key="line" :style="offerStyle" v-for="line in offerLines">
          {{ line }}
        </p>
      </div>
    </div>

    <a
      @click="handleClick"
      target="_blank"
      :href="link"
      :class="[classes.offerButton]"
    >
      {{ buttonText }} <span :class="classes.externalLinkIcon" />
    </a>
  </div>
</template>

<script lang="ts">
import { createUseStyles } from "@fresh8/vue-jss/src";
import FontFill from "fontfill";
import { computed, defineComponent, ref, watchEffect } from "vue";
import { useDimensionSync } from "@fresh8/vue-utils/src/dimensionSync";
import WhiteBoulder from "../images/White-Boulder.svg";
import { injectOffer } from "../composition/offer";
import { useHandleAnalytics } from "../lib/analytics";
import { AnalyticEvents } from "../types/analytics";

const { AutoFittingText } = FontFill;
const useStyles = createUseStyles({
  marketCard: {
    border: "1px solid #dcdddf",
    borderRadius: "10px",
    display: "flex",
    gridTemplateRows: "18px 28px 20px 2fr 36px",
    height: "100%",
    boxSizing: "border-box",
    flex: "1 1 0%",
    flexDirection: "column",
    padding: "8px"
  },
  header: {
    textAlign: "center",
    fontSize: "12px",
    lineHeight: "14px",
    margin: "7px 0",
    fontWeight: 800
  },
  offerWrapper: {
    flex: "1 1 0",
    minHeight: "0px",
    padding: "0 10px 8px",
    overflow: "hidden",
    boxSizing: "border-box"
  },
  offerText: {
    textAlign: "center",
    display: "flex",
    justifyContent: "center",
    flexDirection: "column",
    height: "100%",
    fontWeight: 400
  },
  offerButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    height: 30,
    boxSizing: "border-box",
    padding: "3px 0",
    color: "#ffffff",
    backgroundColor: "#0066CC",
    borderRadius: "4px",
    fontSize: "11px",
    lineHeight: "1.33",
    fontWeight: 600
  },
  externalLinkIcon: {
    backgroundImage: `url('${WhiteBoulder}')`,
    backgroundRepeat: "no-repeat",
    display: "inline-block",
    width: "8px",
    height: "8px",
    marginLeft: "3px"
  }
});

export default defineComponent({
  name: "OfferCard",
  components: {},
  setup() {
    const classes = useStyles(ref(undefined));
    const { offer } = injectOffer();

    const fontFamily =
      "'Roboto','Arial','Helvetica Neue','Helvetica',sans-serif";

    const offerTextRef = ref();
    const { dimensions: offerTextBox } = useDimensionSync(offerTextRef);
    const fontFitter = new AutoFittingText(
      offerTextBox.value.width,
      offerTextBox.value.height,
      {
        targetString: offer.value.body,
        family: fontFamily,
        fontWeight: "400",
        lineHeight: 1.2,
        maxFontSize: 20,
        minFontSize: 12
      }
    );

    const fontMetrics = ref<any>();

    watchEffect(() => {
      fontFitter.width = offerTextBox.value.width;
      fontFitter.height = offerTextBox.value.height;
      fontFitter.targetString = offer.value.body;
      fontFitter.family = fontFamily;

      fontMetrics.value = fontFitter.metrics;

      const fontDefString = `12px ${fontFamily}`;

      document.fonts &&
        !document.fonts.check(fontDefString, fontFitter.targetString) &&
        document.fonts
          .load(`12px ${fontFamily}`, fontFitter.targetString)
          .then(() => {
            // trigger a recalculation
            // should make fontfill more functional
            fontFitter.family = "";
            fontFitter.family = fontFamily;
            fontMetrics.value = fontFitter.metrics;
          });
    });

    const $handleAnalytics = useHandleAnalytics();

    const handleClick = computed(() => () => {
      const statsEvent = AnalyticEvents.CLICKED_THROUGH;
      const statsDesc = "Offer Card";

      $handleAnalytics(statsEvent, statsDesc, []);
    });

    return {
      handleClick,
      classes,
      offerTextRef,
      body: offer.value,
      link: offer.value.link,
      header: offer.value.header,
      buttonText: offer.value.buttonText,
      offerStyle: computed(() => ({
        fontSize: fontMetrics.value.fontSize + "px",
        lineHeight: 1.2,
        margin: 0,
        padding: 0,
        fontFamily
      })),
      offerLines: computed(() => fontMetrics.value.lines as string[])
    };
  }
});
</script>
